import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { mensagemDoErro } from '@/data/http';

interface Opcoes {
  /** Mensagem do toast de confirmação. Ações silenciosas passam `null`. */
  sucesso?: string | null;
  /** Prefixo da mensagem de erro; o detalhe vem sempre do erro real. */
  contextoDoErro: string;
  aoConcluir?: () => void;
}

/**
 * Executa uma ação de escrita mantendo o botão disciplinado:
 * fica `disabled` enquanto roda, o rótulo continua visível (quem troca o ícone
 * é o componente `Button`), e o resultado vira toast — nunca um erro mudo.
 *
 * Erro que exige decisão do usuário não vira toast: é devolvido em `erro` para
 * a tela exibir inline (Playbook § Feedback e progresso).
 */
export function useAsyncAction(opcoes: Opcoes) {
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState<Error | null>(null);

  const executar = useCallback(
    async (acao: () => Promise<void>) => {
      setExecutando(true);
      setErro(null);
      try {
        await acao();
        if (opcoes.sucesso) toast.success(opcoes.sucesso);
        opcoes.aoConcluir?.();
      } catch (causa) {
        const problema = causa instanceof Error ? causa : new Error(String(causa));
        setErro(problema);
        // O backend manda o motivo específico em `message`/`detail`
        // (`NotAcceptableError`) — sem isso, erro de validação de negócio (ex.:
        // lote beta × não-beta) aparecia só como "API respondeu 406", sem dizer o quê.
        toast.error(`${opcoes.contextoDoErro}: ${mensagemDoErro(causa) ?? problema.message}`);
      } finally {
        setExecutando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [opcoes.sucesso, opcoes.contextoDoErro],
  );

  return { executar, executando, erro, limparErro: () => setErro(null) };
}
